import DatePicker from 'components/DatePicker';
import Required from 'components/Required';
import moment from 'moment';
import React, { ChangeEvent } from 'react';
import { FormGroup, Label, Input, Button, Row, Col } from 'reactstrap';
import '../../../assets/css/custom-button.css';
import { MatriculaRequest } from 'types/Matriculas';
import { DatePickerChangeEvent } from 'components/DatePicker';
import CuitInput from 'components/CuitInput';
import { Paises } from 'types/Paises';
import { Provincias } from 'types/Provincias';
import { Partidos } from 'types/Partidos';
import { Localidades } from 'types/Localidades';
import { Delegations } from 'types/Delegations';

interface Props {
  data: Partial<MatriculaRequest>;
  formValidation: Partial<Record<keyof MatriculaRequest, boolean>>;
  handleInputChange: (event: ChangeEvent<HTMLInputElement> | DatePickerChangeEvent) => void;
  isLoading: boolean;
  nacimientoPaises: Paises[],
  nacimientoProvincias: Provincias[];
  nacimientoPartidos: Partidos[];
  nacimientoLocalidades: Localidades[];
  delegations: Delegations[];
  onNext: () => void;
  validateStep: (requiredFields: string[], isForm?: boolean) => boolean;
  disableSend: boolean;
}

const StepOne = ({
  data,
  formValidation,
  handleInputChange,
  isLoading,
  nacimientoPaises,
  nacimientoProvincias,
  nacimientoPartidos,
  nacimientoLocalidades,
  delegations,
  onNext,
  validateStep,
  disableSend,
}: Props) => {
  const validateStepOne = (): boolean => {
    // Lista de campos requeridos en StepOne
    const requiredFields = [
      'nombre',
      'apellido',
      'tipo_documento',
      'nro_documento',
      'cuit',
      'sexo',
      'email',
      'fecha_nac',
      'prefijo_telefono',
      'telefono',
      'pais_nac_id',
      'provincia_nac_id',
      'partido_nac_id',
      'localidad_nac_id',
      'delegacion_nro',
    ];

    return validateStep(requiredFields);
  };

  return (
    <div style={{ padding: '0 10rem 1rem 10rem' }}>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='nombre'
            >
              Nombre <Required />
            </Label>
            <Input
              id='nombre'
              name='nombre'
              value={data.nombre ?? ''}
              invalid={formValidation.nombre === false}
              onChange={handleInputChange}
              disabled={true}
              required
            />
          </Col>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='apellido'
            >
              Apellido <Required />
            </Label>
            <Input
              id='apellido'
              name='apellido'
              value={data.apellido ?? ''}
              invalid={formValidation.apellido === false}
              onChange={handleInputChange}
              disabled={true}
              required
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='tipo_documento'
            >
              Tipo de documento <Required />
            </Label>
            <Input
              id='tipo_documento'
              name='tipo_documento'
              type='select'
              className='custom-select form-control'
              value={data.tipo_documento ?? ''}
              invalid={formValidation.tipo_documento === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            >
              <option disabled value='' hidden>
                Seleccione una opción...
              </option>
              <option key='CI' value='CI'>
                CI
              </option>
              <option key='DNI' value='DNI'>
                DNI
              </option>
              <option key='LC' value='LC'>
                LC
              </option>
              <option key='LE' value='LE'>
                LE
              </option>
            </Input>
          </Col>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='nro_documento'
            >
              Número de documento <Required />
            </Label>
            <Input
              id='nro_documento'
              name='nro_documento'
              inputMode='numeric'
              pattern='\d*'
              invalid={formValidation.nro_documento === false}
              value={data.nro_documento ?? ''}
              onChange={handleInputChange}
              disabled={true}
              required
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='cuit'
            >
              CUIT <Required />
            </Label>
            <CuitInput
              id="cuit"
              name="cuit"
              value={data.cuit ?? ''}
              invalid={formValidation.cuit === false}
              disabled={isLoading}
              required
              onChange={handleInputChange}
            />
          </Col>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='sexo'
            >
              Sexo <Required />
            </Label>
            <Input
              id='sexo'
              name='sexo'
              type='select'
              className='custom-select form-control'
              value={data.sexo ?? ''}
              invalid={formValidation.sexo === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            >
              <option value=''>Seleccione una opción...</option>
              <option key='F' value='F' >Femenino</option>
              <option key='M' value='M' >Masculino</option>
            </Input>
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='email'
            >
              Email <Required />
            </Label>
            <Input
              id='email'
              name='email'
              value={data.email ?? ''}
              invalid={formValidation.email === false}
              onChange={handleInputChange}
              disabled={true}
              required
            />
          </Col>
          <Col sm={2}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='prefijo_telefono'
            >
              Prefijo <Required />
            </Label>
            <Input
              id='prefijo_telefono'
              name='prefijo_telefono'
              inputMode='numeric'
              pattern='\d*'
              value={data.prefijo_telefono ?? ''}
              invalid={formValidation.prefijo_telefono === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
          <Col sm={4}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='telefono'
            >
              Teléfono <Required />
            </Label>
            <Input
              id='telefono'
              name='telefono'
              inputMode='numeric'
              pattern='\d*'
              value={data.telefono ?? ''}
              invalid={formValidation.telefono === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <Row>
          <Col sm={6}>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='fecha_nac'
            >
              Fecha de Nacimiento <Required />
            </Label>
            <DatePicker
              id='fecha_nac'
              name='fecha_nac'
              selected={data.fecha_nac ?? undefined}
              dateFormat='dd/MM/yyyy'
              locale='es'
              placeholderText='Fecha de Nacimiento'
              className='date-picker form-control'
              maxDate={moment().subtract(18, 'years').toDate()}
              disabled={isLoading || disableSend}
              onChange={date => {
                handleInputChange({
                  target: {
                    name: 'fecha_nac',
                    type: 'datepicker',
                    value: date,
                  },
                });
              }}
              autoComplete='off'
              showYearDropdown
              showMonthDropdown
              dropdownMode='scroll'
              required
            />
          </Col>
          <Col md='6'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='pais_nac_id'
            >
              País nacimiento <Required />
            </Label>
            <Input
              id='pais_nac_id'
              name='pais_nac_id'
              type='select'
              className='custom-select form-control'
              value={data.pais_nac_id ?? ''}
              invalid={formValidation.pais_nac_id === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            >
              <option value=''>Seleccione una opción...</option>
              {nacimientoPaises.map((item: any) => {
                return (
                  <option
                    key={item.MTR051_ID_PAIS}
                    value={item.MTR051_ID_PAIS}
                  >
                    { item.MTR051_ID_PAIS == 1
                      ? item.nombrePais
                      : 'Extranjero'
                    }
                  </option>
                );
              })}
            </Input>
          </Col>
        </Row>
      </FormGroup>
      { !!data.pais_nac_id && data.pais_nac_id == 1
        ?
          <>
            <FormGroup>
              <Row>
                <Col md='6'>
                  <Label
                    style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
                    for='provincia_nac_id'
                  >
                    Provincia nacimiento <Required />
                  </Label>
                  <Input
                    id='provincia_nac_id'
                    name='provincia_nac_id'
                    type='select'
                    className='custom-select form-control'
                    value={data.provincia_nac_id ?? ''}
                    invalid={formValidation.provincia_nac_id === false}
                    onChange={handleInputChange}
                    disabled={isLoading || disableSend}
                    required
                  >
                    <option value=''>Seleccione una opción...</option>
                    {nacimientoProvincias.map((item: any) => {
                      return (
                        <option
                          key={item.MTR052_ID_PROVINCIA}
                          value={item.MTR052_ID_PROVINCIA}
                        >
                          {item.MTR052_D_PROVINCIA}
                        </option>
                      );
                    })}
                  </Input>
                </Col>
                <Col md='6'>
                  <Label
                    style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
                    for='partido_nac_id'
                  >
                    Partido nacimiento <Required />
                  </Label>
                  <Input
                    id='partido_nac_id'
                    name='partido_nac_id'
                    type='select'
                    className='custom-select form-control'
                    value={data.partido_nac_id ?? ''}
                    invalid={formValidation.partido_nac_id === false}
                    onChange={handleInputChange}
                    disabled={isLoading || disableSend || !nacimientoPartidos.length}
                    required
                  >
                    <option value=''>Seleccione una opción...</option>
                    {nacimientoPartidos.map((item: any) => {
                      return (
                        <option
                          key={item.MTR038_ID_PARTIDO}
                          value={item.MTR038_ID_PARTIDO}
                        >
                          {item.MTR038_D_PARTIDO}
                        </option>
                      );
                    })}
                  </Input>
                </Col>
              </Row>
            </FormGroup>
            <FormGroup>
              <Row>
                <Col md='6'>
                  <Label
                    style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
                    for='localidad_nac_id'
                  >
                    Localidad nacimiento <Required />
                  </Label>
                  <Input
                    id='localidad_nac_id'
                    name='localidad_nac_id'
                    type='select'
                    className='custom-select form-control'
                    value={data.localidad_nac_id ?? ''}
                    invalid={formValidation.localidad_nac_id === false}
                    onChange={handleInputChange}
                    disabled={isLoading || disableSend || !nacimientoLocalidades.length}
                    required
                  >
                    <option value=''>Seleccione una opción...</option>
                    {nacimientoLocalidades.map((item: any) => {
                      return (
                        <option
                          key={item.MTR008_ID_LOCALIDAD}
                          value={item.MTR008_ID_LOCALIDAD}
                        >
                          {item.MTR008_D_LOCALIDAD}
                        </option>
                      );
                    })}
                  </Input>
                </Col>
              </Row>
            </FormGroup>
          </>
        : null
      }
      <hr className='my-4' />
      <Col sm={12} className='text-center' style={{ marginBottom: '1.5rem' }}>
        <span
          style={{
            fontSize: '1rem',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '1px',
          }}
        >
          Delegación
        </span>
      </Col>
      <FormGroup>
        <Row>
          <Col md='12'>
            <Label
              style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}
              for='delegacion_nro'
            >
              Delegación <Required />
            </Label>
            <Input
              id='delegacion_nro'
              name='delegacion_nro'
              type='select'
              className='custom-select form-control'
              value={data.delegacion_nro ?? ''}
              invalid={formValidation.delegacion_nro === false}
              onChange={handleInputChange}
              disabled={isLoading || disableSend}
              required
            >
              <option disabled value='' hidden>
                Seleccione una opción...
              </option>
              {delegations.map((item: any) => {
                return (
                  <option
                    key={item.MTR006_ID_DELEGACION}
                    value={item.MTR006_ID_DELEGACION}
                  >
                    {item.MTR006_D_NOMBRE}
                  </option>
                );
              })}
            </Input>
          </Col>
        </Row>
      </FormGroup>

      <div style={{ marginTop: 34 }} className='d-flex justify-content-center'>
        <Button
          type='button'
          className='btn-custom'
          disabled={isLoading || disableSend}
          onClick={() => {
            if (validateStepOne()) onNext();
          }}
        >
          Siguiente
        </Button>
      </div>
    </div>
  );
};

export default StepOne;
